import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import {
  UsersThree,
  ListChecks,
  ArrowClockwise,
  CheckCircle,
  Trash,
  Copy,
  Lightning,
} from '@phosphor-icons/react';

const API_URL =
  typeof window !== 'undefined' && window.location.hostname &&
  !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('waitlist');
  const [waitlist, setWaitlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [working, setWorking] = useState(''); // email currently being approved
  const [inviteResult, setInviteResult] = useState(null); // {email, url}

  const isAdmin = user && (user.role === 'admin' || user.email === 'admin@lifeadmin.ai');

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const [w, u, a] = await Promise.all([
        axios.get(`${API_URL}/api/admin/waitlist`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/approved-emails`, { withCredentials: true }),
      ]);
      setWaitlist(w.data.entries || []);
      setUsers(u.data.users || []);
      setApproved(a.data.entries || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const removeWaitlist = async (email) => {
    if (!window.confirm(`Remove ${email} from waitlist?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/waitlist/${encodeURIComponent(email)}`, {
        withCredentials: true,
      });
      setWaitlist((rows) => rows.filter((r) => r.email !== email));
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const approveWaitlist = async (email) => {
    setWorking(email);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/waitlist/${encodeURIComponent(email)}/approve`,
        {},
        { withCredentials: true },
      );
      // If a brand-new email (no account yet), surface the invite link so the
      // admin can paste it to the user via WhatsApp / email / wherever.
      if (res.data?.invite_token) {
        setInviteResult({
          email: res.data.email,
          url: `${window.location.origin}/invite/${res.data.invite_token}`,
        });
      }
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Approve failed');
    } finally {
      setWorking('');
    }
  };

  const revokeApproval = async (email) => {
    if (!window.confirm(`Revoke approval for ${email}? They'll lose access on next login.`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/approved-emails/${encodeURIComponent(email)}`,
        { withCredentials: true },
      );
      setApproved((rows) => rows.filter((r) => r.email !== email));
    } catch (err) {
      alert(err.response?.data?.detail || 'Revoke failed');
    }
  };

  const copyEmails = () => {
    const all = waitlist.map((r) => r.email).join(', ');
    navigator.clipboard.writeText(all);
    setCopied('all');
    setTimeout(() => setCopied(''), 1800);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto py-16 text-center">
          <h1 className="text-2xl font-bold text-[#2d3338]">Admin only</h1>
          <p className="mt-3 text-sm text-[#596065]">
            You need to be logged in as the admin to view this page.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#2d3338]">Admin Console</h1>
            <p className="mt-1 text-sm text-[#596065]">
              Waitlist sign-ups and registered users.
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#ebeef2] bg-white text-[#2d3338] text-sm font-semibold hover:bg-[#f8f9fb] disabled:opacity-50"
            data-testid="admin-refresh"
          >
            <ArrowClockwise size={14} weight="bold" className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Stat tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => setTab('waitlist')}
            className={`text-left p-5 rounded-2xl border transition-all ${
              tab === 'waitlist'
                ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                : 'bg-white border-[#ebeef2] hover:border-blue-200'
            }`}
            data-testid="tab-waitlist"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase font-bold tracking-widest opacity-80">
              <ListChecks size={14} weight="fill" /> Waitlist
            </div>
            <div className="mt-2 text-3xl font-bold">{waitlist.length}</div>
            <div className="text-xs mt-0.5 opacity-80">awaiting approval</div>
          </button>
          <button
            type="button"
            onClick={() => setTab('approved')}
            className={`text-left p-5 rounded-2xl border transition-all ${
              tab === 'approved'
                ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg'
                : 'bg-white border-[#ebeef2] hover:border-emerald-200'
            }`}
            data-testid="tab-approved"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase font-bold tracking-widest opacity-80">
              <CheckCircle size={14} weight="fill" /> Approved
            </div>
            <div className="mt-2 text-3xl font-bold">{approved.length}</div>
            <div className="text-xs mt-0.5 opacity-80">cleared to sign in</div>
          </button>
          <button
            type="button"
            onClick={() => setTab('users')}
            className={`text-left p-5 rounded-2xl border transition-all ${
              tab === 'users'
                ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                : 'bg-white border-[#ebeef2] hover:border-blue-200'
            }`}
            data-testid="tab-users"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase font-bold tracking-widest opacity-80">
              <UsersThree size={14} weight="fill" /> Registered users
            </div>
            <div className="mt-2 text-3xl font-bold">{users.length}</div>
            <div className="text-xs mt-0.5 opacity-80">accounts created</div>
          </button>
        </div>

        {/* Waitlist tab */}
        {tab === 'waitlist' && (
          <div className="bg-white rounded-2xl border border-[#ebeef2] overflow-hidden" data-testid="waitlist-table">
            <div className="px-5 py-3 border-b border-[#ebeef2] flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-[#2d3338]">Waitlist sign-ups</h2>
              <button
                type="button"
                onClick={copyEmails}
                disabled={waitlist.length === 0}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#005bc1] hover:underline disabled:opacity-40 disabled:no-underline"
                data-testid="copy-all-emails"
              >
                {copied === 'all' ? <CheckCircle size={12} weight="fill" /> : <Copy size={12} weight="bold" />}
                {copied === 'all' ? 'Copied!' : 'Copy all emails'}
              </button>
            </div>
            {waitlist.length === 0 ? (
              <p className="p-8 text-sm text-center text-[#596065]">
                No waitlist sign-ups yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fb] text-[11px] uppercase font-bold text-[#596065] tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Source</th>
                      <th className="text-left px-5 py-3">Joined</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map((row) => (
                      <tr key={row.email} className="border-t border-[#f2f4f6] hover:bg-[#f8f9fb]">
                        <td className="px-5 py-3 font-medium text-[#2d3338]">{row.email}</td>
                        <td className="px-5 py-3 text-[#596065]">{row.name || '—'}</td>
                        <td className="px-5 py-3">
                          <span className="text-[11px] font-semibold text-[#005bc1] bg-[#eef4ff] rounded-full px-2 py-0.5">
                            {row.source || 'landing'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-[#596065] text-xs">{fmtDate(row.created_at)}</td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => approveWaitlist(row.email)}
                            disabled={working === row.email}
                            className="inline-flex items-center gap-1 px-2.5 py-1 mr-2 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`approve-${row.email}`}
                          >
                            <Lightning size={11} weight="fill" />
                            {working === row.email ? '…' : 'Approve'}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeWaitlist(row.email)}
                            className="p-1.5 rounded-lg text-[#acb3b8] hover:text-red-600 hover:bg-red-50"
                            title="Remove"
                            data-testid={`remove-waitlist-${row.email}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Approved tab */}
        {tab === 'approved' && (
          <div className="bg-white rounded-2xl border border-[#ebeef2] overflow-hidden" data-testid="approved-table">
            <div className="px-5 py-3 border-b border-[#ebeef2]">
              <h2 className="text-sm font-bold text-[#2d3338]">Approved emails</h2>
              <p className="text-xs text-[#596065] mt-0.5">
                These emails can sign in. Revoke any time — they'll lose access on next login.
              </p>
            </div>
            {approved.length === 0 ? (
              <p className="p-8 text-sm text-center text-[#596065]">No approvals yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fb] text-[11px] uppercase font-bold text-[#596065] tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Approved by</th>
                      <th className="text-left px-5 py-3">Approved at</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {approved.map((row) => (
                      <tr key={row.email} className="border-t border-[#f2f4f6] hover:bg-[#f8f9fb]">
                        <td className="px-5 py-3 font-medium text-[#2d3338]">{row.email}</td>
                        <td className="px-5 py-3 text-[#596065]">{row.approved_by || '—'}</td>
                        <td className="px-5 py-3 text-[#596065] text-xs">{fmtDate(row.approved_at)}</td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => revokeApproval(row.email)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 text-red-700 text-[11px] font-semibold hover:bg-red-100"
                            data-testid={`revoke-${row.email}`}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users tab */}
        {tab === 'users' && (
          <div className="bg-white rounded-2xl border border-[#ebeef2] overflow-hidden" data-testid="users-table">
            <div className="px-5 py-3 border-b border-[#ebeef2]">
              <h2 className="text-sm font-bold text-[#2d3338]">Registered users</h2>
            </div>
            {users.length === 0 ? (
              <p className="p-8 text-sm text-center text-[#596065]">No users yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8f9fb] text-[11px] uppercase font-bold text-[#596065] tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Auth</th>
                      <th className="text-left px-5 py-3">Role</th>
                      <th className="text-left px-5 py-3">Timezone</th>
                      <th className="text-left px-5 py-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#f2f4f6] hover:bg-[#f8f9fb]">
                        <td className="px-5 py-3 font-medium text-[#2d3338]">{u.email}</td>
                        <td className="px-5 py-3 text-[#596065]">{u.name || '—'}</td>
                        <td className="px-5 py-3">
                          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                            u.auth_provider === 'google'
                              ? 'text-[#0f6b3a] bg-[#ecfdf5]'
                              : 'text-[#596065] bg-[#f2f4f6]'
                          }`}>
                            {u.auth_provider || 'local'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {u.role === 'admin' ? (
                            <span className="text-[11px] font-bold text-[#7c3aed] bg-[#ede9fe] rounded-full px-2 py-0.5">
                              admin
                            </span>
                          ) : (
                            <span className="text-[11px] text-[#596065]">user</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-[#596065] text-xs">{u.timezone || '—'}</td>
                        <td className="px-5 py-3 text-[#596065] text-xs">{fmtDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite link modal — shown after approving a brand-new email */}
      {inviteResult && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          onClick={() => setInviteResult(null)}
          data-testid="invite-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl"
          >
            <div className="inline-flex w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-3">
              <CheckCircle size={26} weight="fill" />
            </div>
            <h3 className="text-xl font-bold text-[#2d3338]">Approved! Share this invite link</h3>
            <p className="mt-2 text-sm text-[#596065]">
              Send <span className="font-semibold text-[#2d3338]">{inviteResult.email}</span> the link
              below. They'll open it, set a password, and be logged in. Or they can sign in with Google
              using the same email — both work.
            </p>
            <div className="mt-4 p-3 rounded-xl bg-[#f8f9fb] border border-[#ebeef2] flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteResult.url}
                className="flex-1 bg-transparent text-[12px] text-[#2d3338] font-mono outline-none"
                data-testid="invite-url-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(inviteResult.url);
                  setCopied('invite');
                  setTimeout(() => setCopied(''), 1800);
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 whitespace-nowrap"
                data-testid="copy-invite-url"
              >
                {copied === 'invite' ? <CheckCircle size={12} weight="fill" /> : <Copy size={12} weight="bold" />}
                {copied === 'invite' ? 'Copied!' : 'Copy link'}
              </button>
            </div>
            <p className="mt-3 text-[11px] text-[#596065]">
              Link valid for 14 days. One-time use — once they set a password, the link expires.
            </p>
            <button
              type="button"
              onClick={() => setInviteResult(null)}
              className="mt-5 px-4 py-2 rounded-xl bg-slate-100 text-slate-900 text-sm font-semibold hover:bg-slate-200"
              data-testid="invite-modal-close"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </Layout>
  );
};

export default Admin;
